/**
 * "Sofőr a járműnél" (owner's decision, 2026-11-05; feature `vehicleDriver`, off by default): when not the owner
 * brings and takes the car (a company car's employee, a family member), the vehicle keeps the driver's name, mobile
 * and e-mail. The driver may get only two messages: the "car ready" notice (never with an amount) and the intake
 * information. Quotes, approval, invoices, prices and reminders go only to the customer.
 *
 * Pure rules shared by the app, the tests and the docs; the send-quote edge function and the database enforce the
 * same (supabase/migrations/20261224000000_vehicle_driver.sql, textHasMoneyAmount in _shared/messaging.ts).
 */

/** Who a customer message goes to. */
export type MessageRecipient = "customer" | "driver";
export const MESSAGE_RECIPIENTS: readonly MessageRecipient[] = ["driver", "customer"];

/** The messages of the program to the outside world. */
export type CustomerMessageKind = "ready" | "intake" | "quote" | "declinedWork" | "invoice" | "reminder";

/** The only messages the driver may get (owner, 2026-11-05); everything about money stays with the customer. */
export const DRIVER_MESSAGES: readonly CustomerMessageKind[] = ["ready", "intake"];

export const driverMayReceive = (kind: CustomerMessageKind) => DRIVER_MESSAGES.includes(kind);

export interface DriverContact {
  driverName?: string | null;
  driverMobile?: string | null;
  driverEmail?: string | null;
}

const filled = (v: string | null | undefined) => typeof v === "string" && v.trim() !== "";

/** The driver can be reached: a mobile number or an e-mail address (a name alone is not enough). */
export function hasDriverContact(v: DriverContact | null | undefined): boolean {
  return !!v && (filled(v.driverMobile) || filled(v.driverEmail));
}

/** Anything of the driver is filled (the section on the vehicle page shows it). */
export function hasDriver(v: DriverContact | null | undefined): boolean {
  return !!v && (filled(v.driverName) || hasDriverContact(v));
}

/**
 * Which recipients a message is offered to, and which are ticked first. Without a reachable driver (or with the
 * option off) only the customer, ticked, as before. The "car ready" notice goes to the one who takes the car: the
 * driver ticked, the customer not. The intake information goes to both (the customer as before, and the driver).
 */
export function recipientDefaults(kind: CustomerMessageKind, driverReachable: boolean): { key: MessageRecipient; ticked: boolean }[] {
  if (!driverReachable || !driverMayReceive(kind)) return [{ key: "customer", ticked: true }];
  return [
    { key: "driver", ticked: true },
    { key: "customer", ticked: kind !== "ready" },
  ];
}

/** The amount of a "car ready" notice: never for the driver, for the customer as the workshop set it. */
export function readyAmountFor(recipient: MessageRecipient, amount: number | null): number | null {
  return recipient === "driver" ? null : amount;
}

/** The orange warning next to the amount: an amount is in the notice and the driver is among the recipients. */
export const driverAmountWarning = (amount: number | null, driverOffered: boolean) => amount !== null && driverOffered;
